import { Loader2 } from "lucide-react";
import type { CarouselBgCandProgress } from "@/lib/api";
import { estimateBgCandProgress } from "@/lib/bgCandProgress";
import { clockLabel, useNowTick } from "@/lib/useNowTick";
import { cn } from "@/lib/utils";

export default function BgCandidatesProgress({
  progress,
  running,
  className,
}: {
  progress?: CarouselBgCandProgress | null;
  running?: boolean;
  className?: string;
}) {
  const phase = progress?.phase || (running ? "waiting" : "");
  const active = Boolean(running) && phase !== "done" && phase !== "error" && phase !== "failed";
  const now = useNowTick(active || Boolean(running && progress?.started_at));
  if (!progress?.started_at && !running) return null;

  const live = estimateBgCandProgress(
    {
      ...(progress || {}),
      started_at: progress?.started_at || new Date(now).toISOString(),
      total: progress?.total || 3,
      phase: phase || "waiting",
    },
    now,
  );
  const pct = phase === "done" ? 100 : live.percent;
  const total = Math.max(1, Number(progress?.total || 3));
  const done = Math.max(0, Number(progress?.done || 0));
  const current = Math.min(total, Math.max(1, Number(progress?.current || done + 1)));
  const samples = Number(progress?.samples || 0);
  const segments = live.segments.length ? live.segments : [];
  const title =
    phase === "done"
      ? "Warianty gotowe"
      : phase === "error" || phase === "failed"
        ? "Generacja przerwana"
        : progress?.detail || `Generuję wariant ${current}/${total}`;

  return (
    <div
      className={cn("rounded-md border border-border bg-muted/30 p-3", className)}
      aria-live="polite"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 font-medium">
          {active ? <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" /> : null}
          {active ? `${current}/${total} · ` : null}
          {title}
        </span>
        <span className="tabular-nums text-muted-foreground">{pct}%</span>
      </div>
      {segments.length > 1 ? (
        <div className="flex h-3 gap-1">
          {segments.map((seg) => (
            <div key={seg.id} className="relative h-full min-w-0 flex-1 overflow-hidden rounded-sm bg-muted">
              <div
                className="absolute inset-y-0 left-0 bg-brand transition-all"
                style={{ width: `${Math.round(Number(seg.fraction || 0) * 100)}%` }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      {segments.length > 1 ? (
        <div className="mt-1 flex gap-0.5 text-[10px] text-muted-foreground">
          {segments.map((seg) => (
            <span
              key={`l-${seg.id}`}
              className={cn(
                "min-w-0 flex-1 truncate",
                seg.id === `v${current}` && active ? "font-medium text-foreground" : "",
              )}
            >
              {seg.label}
            </span>
          ))}
        </div>
      ) : null}
      <p className="mt-2 text-xs text-muted-foreground">
        minęło {clockLabel(live.elapsed_sec)}
        {active ? ` · zostało ok. ${clockLabel(live.eta_sec)}` : ""}
        {live.expected_sec > 0 && active ? ` · średnio ~${clockLabel(live.expected_sec)}` : ""}
        {active ? ` · ~${clockLabel(Math.round(live.ms_per_variant / 1000))} / wariant` : ""}
        {samples > 0
          ? ` · średnia z ${samples} ${samples === 1 ? "strzału" : "strzałów"}`
          : " · pierwsza generacja, szacunek startowy"}
      </p>
      {progress?.error ? <p className="mt-1 text-xs text-destructive">{progress.error}</p> : null}
    </div>
  );
}
